// Validation Step Types for Enhanced Skilled Maker Journey

import { BusinessIdea, SurveyQuestion } from './ai';
import { QuestionAnswer } from '../services/questionnaireService';

export interface ValidationStepData {
  selectedIdea: BusinessIdea;
  surveyId?: string;
  responses: ValidationResponse[];
  successMetrics: ValidationSuccessMetrics;
  meetsSuccessCriteria: boolean;
  completedAt?: Date;
}

export interface ValidationResponse {
  questionId: string;
  selectedOptions: string[];
  timestamp: Date;
}

export interface ValidationSuccessMetrics {
  problemFrequency: number; // Percentage who experience problem weekly/daily
  trialLikelihood: number;  // Percentage likely to try the solution
  pricingSignal: boolean;   // Clear pricing indication from responses
  totalResponses: number;   // Total number of survey responses
}

export interface ValidationSurveyTemplate {
  title: string;
  description: string;
  questions: ValidationQuestion[];
  successCriteria: ValidationSuccessCriteria;
}

export interface ValidationQuestion {
  id: string;
  text: string;
  type: 'multiple-choice' | 'text' | 'rating';
  options?: string[];
  required: boolean;
  successMapping: ValidationSuccessMapping;
}

export interface ValidationSuccessMapping {
  metric: 'problemFrequency' | 'trialLikelihood' | 'pricingSignal';
  successOptions: string[]; // Options that count as "success" for this metric
}

export interface ValidationSuccessCriteria {
  problemFrequencyThreshold: number; // e.g., 40% (0.4)
  trialLikelihoodThreshold: number;  // e.g., 50% (0.5)
  minimumResponses: number;          // e.g., 10 responses minimum
}

export interface ValidationStepProps {
  idea?: BusinessIdea;
  selectedIdea?: BusinessIdea; // Alternative prop name
  step?: any;
  onComplete?: (data: any) => void | Promise<void>;
  onValidationComplete?: (results: ValidationStepData) => void;
  selectedIdeas?: BusinessIdea[];
  previousStepData?: Record<string, any>;
  onBack?: () => void;
}

// Pre-defined validation survey template
export const DEFAULT_VALIDATION_TEMPLATE: ValidationSurveyTemplate = {
  title: 'Would You Use This?',
  description: 'Help us validate this business idea by answering a few quick questions.',
  questions: [
    {
      id: 'problem_frequency',
      text: 'How often do you encounter this type of problem?',
      type: 'multiple-choice',
      options: [
        'Daily',
        'Weekly', 
        'Monthly',
        'Rarely',
        'Never'
      ],
      required: true,
      successMapping: {
        metric: 'problemFrequency',
        successOptions: ['Daily', 'Weekly']
      }
    },
    {
      id: 'current_solution',
      text: 'What do you currently use to solve this problem?',
      type: 'multiple-choice',
      options: [
        'Manual process (time-consuming)',
        'Existing software (not ideal)',
        'Workaround solution',
        'Nothing - I just deal with it',
        'This problem doesn\'t affect me'
      ],
      required: true,
      successMapping: {
        metric: 'problemFrequency',
        successOptions: ['Manual process (time-consuming)', 'Existing software (not ideal)', 'Workaround solution']
      }
    },
    {
      id: 'trial_likelihood',
      text: 'How likely would you be to try this solution?',
      type: 'multiple-choice',
      options: [
        'Very likely - I need this now',
        'Somewhat likely - sounds useful',
        'Maybe - depends on the details',
        'Probably not - not a priority',
        'Definitely not - not interested'
      ],
      required: true,
      successMapping: {
        metric: 'trialLikelihood',
        successOptions: ['Very likely - I need this now', 'Somewhat likely - sounds useful']
      }
    },
    {
      id: 'pricing_expectation',
      text: 'What would you expect to pay for this solution monthly?',
      type: 'multiple-choice',
      options: [
        'Free (with ads or limitations)',
        '$1-10 per month',
        '$11-25 per month',
        '$26-50 per month',
        '$50+ per month'
      ],
      required: true,
      successMapping: {
        metric: 'pricingSignal',
        successOptions: ['$1-10 per month', '$11-25 per month', '$26-50 per month', '$50+ per month']
      }
    }
  ],
  successCriteria: {
    problemFrequencyThreshold: 0.4, // 40%
    trialLikelihoodThreshold: 0.5,  // 50%
    minimumResponses: 10
  }
};